import { ROUTES } from '#/config/routes';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import {
  readReturnDateRangeFilter,
  resolveReturnDateFilterChipDisplay,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/returnDateListFilter';
import {
  STOCK_RETURN_STATUS_FILTER_OPTIONS,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/returnStatus';
import { searchWarehouseFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { formatDateOrDash } from '#/utils/date';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function formatWarehouseLabel(warehouse) {
  if (!warehouse) {
    return '-';
  }
  return [warehouse.code, warehouse.name].filter(Boolean).join(' ') || '-';
}

export function buildStockReturnColumns({
  filters = {},
  onFilterChange,
  onReturnDateRangeChange,
  includeNavLinks = false,
  sourceWarehouseLabelCache,
  targetWarehouseLabelCache,
  canShowEdit,
} = {}) {
  const navColumn = includeNavLinks
    ? [
      createDocumentNavLinksColumn({
        getDetailPath: (row) => ROUTES.workOrderReturnDetail(row.id),
        getEditPath: (row) => ROUTES.workOrderReturnEdit(row.id),
        canShowEdit,
      }),
    ]
    : [];

  return [
    ...navColumn,
    {
      id: 'returnNo',
      header: '退料單號',
      accessorKey: 'returnNo',
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'returnNo',
        label: '退料單號',
        filterValue: filters.returnNo,
        onChange: (value) => onFilterChange('returnNo', value),
        placeholder: '搜尋退料單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <DocumentStatusBadge status={row.original.status} size="sm" />
      ),
      size: 96,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: STOCK_RETURN_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      header: '退料日',
      accessorKey: 'returnDate',
      accessorFn: (row) => formatDateOrDash(row.returnDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'returnDateFrom',
        label: '退料日',
        mode: 'range',
        filterValue: readReturnDateRangeFilter(filters),
        onChange: onReturnDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveReturnDateFilterChipDisplay,
      }),
    },
    {
      id: 'sourceWarehouse',
      header: '來源倉',
      accessorFn: (row) => formatWarehouseLabel(row.sourceWarehouse),
      size: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'sourceWarehouseId',
          label: '來源倉',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.sourceWarehouseId,
          onChange: (value) => onFilterChange('sourceWarehouseId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(sourceWarehouseLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(sourceWarehouseLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'targetWarehouse',
      header: '目的倉',
      accessorFn: (row) => formatWarehouseLabel(row.targetWarehouse),
      size: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'targetWarehouseId',
          label: '目的倉',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.targetWarehouseId,
          onChange: (value) => onFilterChange('targetWarehouseId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(targetWarehouseLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(targetWarehouseLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'issueReason',
      header: '退料原因',
      accessorKey: 'issueReason',
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'issueReason',
          label: '退料原因',
          filterValue: filters.issueReason,
          onChange: (value) => onFilterChange('issueReason', value),
          placeholder: '搜尋退料原因',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original?.issueReason || '—'} />
      ),
    },
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original?.remark || '—'} />
      ),
    },
    {
      id: 'lineCount',
      header: '項目數',
      accessorKey: 'lineCount',
      size: 100,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
    },
  ];
}
